import Big from "big.js";
import React from "react";
import { getContainer } from "../../ioc/IOCSetup";
import { CurrencyRateService } from "../../services/CurrencyRateService";


interface Props {
    sourceCurrency?: string;
    sourceAmount?: string | number;
    className?: string;
}

interface State {
    euroRate: number | null;
    isLoading: boolean;
    error: boolean;
}


export class CurrencyEuroValue extends React.Component<Props, State> {
    private currencyRateService: CurrencyRateService;

    constructor(props: Props) {
        super(props);
        this.state = {
            euroRate: null,
            isLoading: true,
            error: false
        };

        this.currencyRateService = getContainer().getCurrencyRateService();
    }

    componentDidMount() {
        this.loadExchangeRate();
    }

    componentDidUpdate(prevProps: Props) {
        // Reload rate if currency changes, or if amount changes (and we have a valid amount)
        const currencyChanged = prevProps.sourceCurrency !== this.props.sourceCurrency;
        const amountChanged = prevProps.sourceAmount !== this.props.sourceAmount;
        
        if (currencyChanged || (amountChanged && this.props.sourceAmount && this.props.sourceAmount !== "")) {
            this.loadExchangeRate();
        }
    }

    private async loadExchangeRate() {
        const { sourceCurrency, sourceAmount } = this.props;

        // Early return if no currency or amount, or if already EUR
        if (!sourceCurrency || !sourceAmount || sourceAmount === "" || sourceCurrency === "EUR") {
            this.setState({ isLoading: false, error: false, euroRate: null });
            return;
        }

        this.setState({ isLoading: true, error: false });

        try {
            const rate = await this.currencyRateService.getCryptoAndFiatToEuroExchangeRates(sourceCurrency);
            this.setState({ 
                euroRate: rate, 
                isLoading: false, 
                error: false 
            });
        } catch (e) {
            // Gracefully hide when exchange rates unavailable
            this.setState({ 
                euroRate: null, 
                isLoading: false, 
                error: true 
            });
        }
    }

    private calculateEuroValue(amount: string | number, rate: number): string {
        try {
            const eurAmount = Big(amount).mul(Big(rate));
            return `EUR ${eurAmount.toFixed(2)}`;
        } catch (e) {
            return "";
        }
    }

    render() {
        const { sourceCurrency, sourceAmount } = this.props;
        const { euroRate, isLoading, error } = this.state;

        // Don't render anything if no currency/amount, already EUR, loading, or error
        if (!sourceCurrency || !sourceAmount || sourceAmount === "" || 
            sourceCurrency === "EUR" || isLoading || error || !euroRate) {
            return null;
        }

        const euroValue = this.calculateEuroValue(sourceAmount, euroRate);
        
        if (!euroValue) {
            return null;
        }

        const className = this.props.className || "currency-eur-value";
        return <div className={className}>{euroValue}</div>;
    }

}


